/**
 * Gerenciador de Sessão
 * Controla o ciclo de vida da sessão do usuário
 */

import { platformServiceFactory, StorageType } from '../../../platform';
import { AppLifecycleEvent } from '../events/lifecycle-events';


export class SessionManager {
  private static inactivityTimer: NodeJS.Timeout | null = null;
  private static lastActivity = Date.now();
  private static sessionTimeout = 15 * 60 * 1000; // 15 minutos em ms
  private static onSessionTimeout: (() => void) | null = null;
  private static isInitialized = false;

  static initialize(options?: {
    sessionTimeoutMinutes?: number;
    onSessionTimeout?: () => void;
  }): void {
    if (this.isInitialized) return;

    if (options?.sessionTimeoutMinutes) {
      this.sessionTimeout = options.sessionTimeoutMinutes * 60 * 1000;
    }

    if (options?.onSessionTimeout) {
      this.onSessionTimeout = options.onSessionTimeout;
    }

    // Adiciona listeners para atividade do usuário
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.recordActivity.bind(this), { passive: true });
    });

    this.startInactivityTimer();
    this.isInitialized = true;
  }

  static recordActivity(): void {
    this.lastActivity = Date.now();
    this.restartInactivityTimer();
  }

  private static startInactivityTimer(): void {
    this.inactivityTimer = setTimeout(() => {
      if (this.onSessionTimeout) {
        this.onSessionTimeout();
      }
    }, this.sessionTimeout);
  }

  private static restartInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.startInactivityTimer();
  }

  static dispose(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.recordActivity.bind(this));
    });

    this.isInitialized = false;
  }

  static get timeSinceLastActivity(): number {
    return Date.now() - this.lastActivity;
  }

  static get isSessionActive(): boolean {
    return this.timeSinceLastActivity < this.sessionTimeout;
  }

  static get lastActivityTime(): Date {
    return new Date(this.lastActivity);
  }
}
